import { AnyObject } from "final-form";
import i18next from "config/i18next";
import { ValidateTimeIntervalParamsType } from "utils/types";
import {
  validateHourDifference,
  validateIsAfterNow,
  validateTimeInterval,
} from "utils/validations";
import * as yup from "yup";
import "./i18n";
import "../slotCreationModal/i18n";

const requiredMessage = i18next.t("validations:required");

const validationHourParams = {
  minDifference: 30,
  minMessage: i18next.t("slotCreation:minTimeDifferenceError"),
  maxDifference: 360,
  maxMessage: i18next.t("slotCreation:maxTimeDifferenceError"),
  field: "slotTime.end",
};

const validationTimeIntervalParams: ValidateTimeIntervalParamsType = {
  message: i18next.t("slotCreation:timeIntervalError"),
  field: "",
  interval: 15,
};

const validateSlotTime = (context: yup.TestContext<AnyObject>) =>
  validateHourDifference(context, validationHourParams);

const validateSlotIntervalTime = (
  value: string,
  context: yup.TestContext<AnyObject>,
  field: string
) =>
  validateTimeInterval(value, context, {
    ...validationTimeIntervalParams,
    field,
  });

const slotTime = yup.object({
  initial: yup
    .string()
    .test("timeValidation", (value, context) => validateSlotTime(context))
    .test("intervalValidation", (val, ctx) =>
      validateSlotIntervalTime(val || "", ctx, "slotTime.initial")
    )
    .required(requiredMessage),
  end: yup
    .string()
    .test("timeValidation", (value, context) => validateSlotTime(context))
    .test("intervalValidation", (val, ctx) =>
      validateSlotIntervalTime(val || "", ctx, "slotTime.end")
    )
    .required(requiredMessage),
});

const validateCapacity = (
  context: yup.TestContext<AnyObject>,
  value?: number
) => {
  const { createError } = context;
  const form = context.parent;
  const field = "capacity";
  if (value !== undefined && value < form.enroled + form.connected)
    return createError({
      message: i18next.t("slotDetail:minCapacityError"),
      path: field,
    });
  return true;
};

export const validationSchema = yup.object({
  capacity: yup
    .number()
    .test("moreThanActual", (value, context) =>
      validateCapacity(context, value)
    )
    .required(requiredMessage),
  slotTime: slotTime.test("inProgress", (value, ctx) =>
    validateIsAfterNow(value.initial, ctx, "slotTime.initial")
  ),
});

export const validationSchemaOnGoing = (
  initialCapacity: number,
  initialEndTime: string
) =>
  yup.object({
    capacity: yup
      .number()
      .test(
        "moreThanPrevious",
        i18next.t("slotDetail:reduceCapacityError", {
          amount: initialCapacity,
        }),
        (value) => !!(value! >= initialCapacity)
      )
      .required(requiredMessage),
    slotTime: yup.object({
      end: yup
        .string()
        .test("timeValidation", (value, context) => validateSlotTime(context))
        .test("intervalValidation", (val, ctx) =>
          validateSlotIntervalTime(val || "", ctx, "slotTime.end")
        )
        .test(
          "intervalValidation",
          i18next.t("slotDetail:reduceSlotTimeError"),
          (value) => {
            if (!value) return true;
            const [hour, minutes] = value.split(":");
            const h = Number(hour);
            const m = Number(minutes);
            const totalMin = h * 60 + m;
            const [prevHour, prevMinutes] = initialEndTime.split(":");
            const prevH = Number(prevHour);
            const prevM = Number(prevMinutes);
            const prevTotalMin = prevH * 60 + prevM;
            return totalMin >= prevTotalMin;
          }
        )
        .required(requiredMessage),
    }),
  });
